import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Phone, MapPin, User, Building2, Calendar as CalIcon, Trash2, Pencil, X, Check, Wallet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import {
  SECTORS, NEEDS, STATUSES, sectorLabel, needLabel,
  formatDateTime, toDatetimeLocal, fromDatetimeLocal,
} from "@/lib/constants";
import { toast } from "sonner";

const inputCls =
  "w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 placeholder:text-slate-400 transition-colors";

const InfoRow = ({ Icon, label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-1.5 flex items-center gap-2">
      {Icon && <Icon size={12} />} {label}
    </div>
    <div className="text-sm text-slate-900">{children || <span className="text-slate-400">—</span>}</div>
  </div>
);

export default function CallDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [note, setNote] = useState("");
  const [noteStatus, setNoteStatus] = useState("islemde");
  const [noteAt, setNoteAt] = useState(toDatetimeLocal(new Date().toISOString()));
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calls/${id}`);
      setCall(data);
      setForm({
        phone: data.phone,
        brand_name: data.brand_name,
        contact_name: data.contact_name,
        sector: data.sector,
        sector_other: data.sector_other || "",
        needs: data.needs || [],
        price_info: data.price_info || "",
        location: data.location || "",
        created_at: toDatetimeLocal(data.created_at),
        current_status: data.current_status,
      });
    } catch (err) {
      toast.error("Kayıt yüklenemedi");
      nav("/calls");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const toggleNeed = (v) => setForm((p) => ({ ...p, needs: p.needs.includes(v) ? p.needs.filter((x) => x !== v) : [...p.needs, v] }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, created_at: fromDatetimeLocal(form.created_at) };
      const { data } = await api.put(`/calls/${id}`, payload);
      setCall(data);
      setEditing(false);
      toast.success("Kayıt güncellendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addUpdate = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/calls/${id}/updates`, {
        note: note.trim(),
        status: noteStatus,
        timestamp: fromDatetimeLocal(noteAt) || new Date().toISOString(),
      });
      setCall(data);
      setNote("");
      setNoteStatus("islemde");
      setNoteAt(toDatetimeLocal(new Date().toISOString()));
      toast.success("Güncelleme eklendi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteCall = async () => {
    await api.delete(`/calls/${id}`);
    toast.success("Kayıt silindi");
    nav("/calls");
  };

  const deleteUpdate = async (uid) => {
    const { data } = await api.delete(`/calls/${id}/updates/${uid}`);
    setCall(data);
  };

  if (loading || !call) return <Layout><div className="text-sm text-slate-500" data-testid="detail-loading">Yükleniyor...</div></Layout>;

  return (
    <Layout>
      <div className="mb-6 flex items-center justify-between" data-testid="detail-header">
        <Link to="/calls" className="text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-2" data-testid="detail-back">
          <ArrowLeft size={14} /> Tüm Çağrılar
        </Link>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <button
                onClick={() => setEditing(true)}
                data-testid="detail-edit"
                className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-medium transition-colors duration-150 flex items-center gap-2"
              >
                <Pencil size={14} /> Düzenle
              </button>
              <button
                onClick={() => setConfirmDel(true)}
                data-testid="detail-delete"
                className="rounded-full bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2 text-sm font-medium transition-colors duration-150 flex items-center gap-2"
              >
                <Trash2 size={14} /> Sil
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setEditing(false); load(); }}
                data-testid="edit-cancel"
                className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-4 py-2 text-sm font-medium transition-colors duration-150 flex items-center gap-2"
              >
                <X size={14} /> İptal
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                data-testid="edit-save"
                className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-4 py-2 text-sm font-medium transition-colors duration-150 flex items-center gap-2 disabled:opacity-50"
              >
                <Check size={14} /> {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-8 mb-8" data-testid="detail-card">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl tracking-tight font-bold text-slate-900" data-testid="detail-brand">{call.brand_name}</h1>
            <p className="text-sm text-slate-500 mt-2">Kayıt Tarihi: {formatDateTime(call.created_at)}</p>
          </div>
          <StatusBadge status={call.current_status} testId="detail-status" />
        </div>

        {!editing ? (
          <div className="grid grid-cols-3 gap-x-8 gap-y-6">
            <InfoRow Icon={Phone} label="Telefon" testId="info-phone">{call.phone}</InfoRow>
            <InfoRow Icon={User} label="Yetkili" testId="info-contact">{call.contact_name}</InfoRow>
            <InfoRow Icon={Building2} label="Sektör" testId="info-sector">{sectorLabel(call.sector, call.sector_other)}</InfoRow>
            <InfoRow Icon={MapPin} label="Lokasyon" testId="info-location">{call.location}</InfoRow>
            <InfoRow Icon={Wallet} label="Fiyat Bilgisi" testId="info-price">{call.price_info}</InfoRow>
            <InfoRow Icon={CalIcon} label="Son Güncelleme" testId="info-updated">{formatDateTime(call.updated_at)}</InfoRow>
            <div className="col-span-3">
              <div className="text-xs font-semibold tracking-wider uppercase text-slate-400 mb-2">İhtiyaç Türü</div>
              <div className="flex flex-wrap gap-2">
                {(call.needs || []).length === 0 && <span className="text-sm text-slate-400">—</span>}
                {(call.needs || []).map((n) => (
                  <span key={n} className="inline-flex px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{needLabel(n)}</span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Telefon</div>
              <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Marka</div>
              <input className={inputCls} value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} data-testid="edit-brand" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Yetkili</div>
              <input className={inputCls} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="edit-contact" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Sektör</div>
              <select className={inputCls} value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="edit-sector">
                {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {form.sector === "diger" && (
                <input className={`${inputCls} mt-3`} value={form.sector_other} onChange={(e) => setForm({ ...form, sector_other: e.target.value })} placeholder="Sektörünüzü yazın" data-testid="edit-sector-other" />
              )}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Lokasyon</div>
              <input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="edit-location" />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Tarih & Saat</div>
              <input type="datetime-local" className={inputCls} value={form.created_at} onChange={(e) => setForm({ ...form, created_at: e.target.value })} data-testid="edit-datetime" />
            </div>
            <div className="col-span-2">
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">Fiyat Bilgisi</div>
              <input className={inputCls} value={form.price_info} onChange={(e) => setForm({ ...form, price_info: e.target.value })} data-testid="edit-price" />
            </div>
            <div className="col-span-2">
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2 tracking-wider">İhtiyaç Türü</div>
              <div className="flex flex-wrap gap-2">
                {NEEDS.map((n) => {
                  const active = form.needs.includes(n.value);
                  return (
                    <button
                      key={n.value}
                      type="button"
                      onClick={() => toggleNeed(n.value)}
                      data-testid={`edit-need-${n.value}`}
                      className={`inline-flex items-center px-4 py-2 rounded-full border text-sm font-medium transition-colors duration-150 ${
                        active ? "bg-orange-50 border-orange-200 text-orange-600" : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {n.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-8" data-testid="timeline-card">
        <h2 className="text-lg tracking-tight font-semibold text-slate-900 mb-6">Güncelleme Geçmişi</h2>

        <form onSubmit={addUpdate} className="rounded-lg border border-slate-200 bg-slate-50/40 p-5 mb-8" data-testid="update-form">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold tracking-wider uppercase text-slate-400 mb-2">Yeni Güncelleme / Not</label>
              <input
                className={inputCls}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Örn: açmadı, teklif iletildi, başka firmayla anlaşmış..."
                data-testid="update-note"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-wider uppercase text-slate-400 mb-2">Tarih & Saat</label>
              <input
                type="datetime-local"
                value={noteAt}
                onChange={(e) => setNoteAt(e.target.value)}
                data-testid="update-datetime"
                className={inputCls}
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const active = noteStatus === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setNoteStatus(s.value)}
                    data-testid={`update-status-${s.value}`}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-medium transition-colors duration-150 ${
                      active ? s.chip : "border-slate-200 text-slate-600 hover:bg-slate-50 bg-white"
                    }`}
                  >
                    <span className={`status-dot ${s.dot}`} />
                    {s.label}
                  </button>
                );
              })}
            </div>
            <button
              type="submit"
              disabled={saving || !note.trim()}
              data-testid="update-submit"
              className="rounded-full bg-slate-900 text-white hover:bg-slate-800 px-5 py-2 font-medium text-sm transition-colors duration-150 disabled:opacity-50"
            >
              Güncelleme Ekle
            </button>
          </div>
        </form>

        {(call.updates || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-6" data-testid="timeline-empty">Bu kayıt için henüz güncelleme eklenmemiş.</div>
        ) : (
          <ol className="relative border-l border-slate-200 ml-2" data-testid="timeline-list">
            {call.updates.map((u) => {
              const meta = STATUSES.find((s) => s.value === u.status) || STATUSES[0];
              return (
                <li key={u.id} className="ml-6 pb-6 last:pb-0" data-testid={`timeline-item-${u.id}`}>
                  <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 border-white ${meta.dot}`} />
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 tabular-nums">{formatDateTime(u.timestamp)}</span>
                      <StatusBadge status={u.status} testId={`timeline-badge-${u.id}`} />
                    </div>
                    <button
                      onClick={() => deleteUpdate(u.id)}
                      data-testid={`timeline-delete-${u.id}`}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                      title="Güncellemeyi sil"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="mt-2 text-sm text-slate-800 whitespace-pre-wrap">{u.note}</div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {confirmDel && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="delete-modal">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm max-w-[440px] w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Kaydı sil?</h3>
            <p className="text-sm text-slate-500 mb-6">Bu işlem geri alınamaz. Kayıt ve tüm güncelleme geçmişi kalıcı olarak silinecek.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDel(false)}
                data-testid="delete-cancel"
                className="rounded-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 px-5 py-2 text-sm font-medium transition-colors duration-150"
              >
                İptal
              </button>
              <button
                onClick={deleteCall}
                data-testid="delete-confirm"
                className="rounded-full bg-red-600 text-white hover:bg-red-700 px-5 py-2 text-sm font-medium transition-colors duration-150"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
